import moment, {Moment} from "moment";

export class DateUtils{

    public static readonly invalidDateText = "Invalid date";

    public static getLongDateFormat(){
        return moment().localeData().longDateFormat('L');
    }

    public static getTimeFormat(ampm: boolean){
        return ampm ? 'hh:mm A': 'HH:mm'
    }

    public static isDateFormatValid(date: any){
        return moment(date, DateUtils.getLongDateFormat()).isValid();
    }

    public static getMomentValue(value: any, ampm?: boolean): Moment | null {
        // Handle empty values
        if (value === undefined || value === null || value === '') {
            return null;
        }

        // Handle existing Moment objects
        if (moment.isMoment(value)) {
            return value.isValid() ? value : null;
        }

        // Determine if we're handling a time value or a date value based on ampm parameter
        // ampm is undefined for dates, and true/false for times
        const isTimeComponent = ampm !== undefined;

        if (isTimeComponent) {
            // TIME COMPONENT HANDLING

            // For time values, first check if it's a simple HH:mm format (e.g., "00:10")
            const timeRegex = /^(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\s*([AaPp][Mm]))?$/;
            if (typeof value === 'string' && timeRegex.test(value)) {
                // Extract components
                const match = value.match(timeRegex);
                let hours = parseInt(match![1], 10);
                const minutes = parseInt(match![2], 10);
                const seconds = match![3] ? parseInt(match![3], 10) : 0;
                const meridiem = match![4]?.toLowerCase();

                // Validate ranges before processing
                const maxHours = meridiem ? 12 : 23;
                const minHours = meridiem ? 1 : 0;
                if (hours < minHours || hours > maxHours || minutes > 59 || seconds > 59) {
                    return null;
                }

                // Adjust hours for AM/PM if needed
                if (meridiem === 'pm' && hours < 12) hours += 12;
                if (meridiem === 'am' && hours === 12) hours = 0;

                // Create a moment object directly with the parsed values
                // This avoids the deprecation warning
                return moment({ hour: hours, minute: minutes, second: seconds });
            }

            // Try specific 12h/24h format based on ampm setting
            const timeFormat = ampm ? "hh:mm A" : "HH:mm";
            const strictM = moment(value, timeFormat, true);
            if (strictM.isValid()) {
                return strictM;
            }

            // If specific format fails, try an array of common time formats
            const timeFormats = [
                'HH:mm',       // 24-hour format: 14:30
                'HH:mm:ss',    // 24-hour format with seconds: 14:30:00
                'hh:mm A',     // 12-hour format: 02:30 PM
                'hh:mm a',     // 12-hour format: 02:30 pm
                'hh:mm:ss A',  // 12-hour format with seconds: 02:30:00 PM
                'hh:mm:ss a'   // 12-hour format with seconds: 02:30:00 pm
            ];

            const multiFormatM = moment(value, timeFormats, true);
            if (multiFormatM.isValid()) {
                return multiFormatM;
            }

            return null;
        } else {
            // DATE COMPONENT HANDLING

            // For dates, try with a list of common formats
            const dateFormats = [
                'YYYY-MM-DD',          // ISO format: 2023-01-31
                'MM/DD/YYYY',          // US format: 01/31/2023
                'DD/MM/YYYY',          // UK/EU format: 31/01/2023
                'DD-MM-YYYY',          // Dash separated: 31-01-2023
                'DD.MM.YYYY',          // Dot separated: 31.01.2023
                'YYYY/MM/DD',          // Japanese format: 2023/01/31
                'D MMM YYYY',          // 31 Jan 2023
                'MMM D, YYYY',         // Jan 31, 2023
                'MMMM D, YYYY',        // January 31, 2023
                'D MMMM YYYY'          // 31 January 2023
            ];

            const m = moment(value, dateFormats, true);
            return m.isValid() ? m : null;
        }
    }

}
